import DynamicForm, { FormValuesOutput } from '@policygenius/mortar-dynamic-forms';
import { useCreateDataPointValues, useGetDataPointValues } from 'api/hooks/useDataPointValues';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { completedNavigatorStep } from 'analytics/navigator';
import { StepName } from 'analytics/navigator/constants';
import { frontendAnalyticsTrack } from 'analytics/utils/segment';
import DynamicFormWrapper from 'components/DynamicFormWrapper';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import {
  BANNER_HORIZON_URN,
  BANNER_RISETERM_URN,
} from 'containers/Estimates/OffboardingEstimates/constants';
import { isEligibleForApplyOnline } from 'containers/MyQuotes/util/applyOnline';
import handleSplitFunnel from 'containers/utils/handleSplitFunnel';
import { APPLICATION_FULL_PATH, MOTIVATIONS_FULL_PATH } from 'routes/constants';
import useSelector from 'store/useSelector';
import { getFeatureExperiment, useFeatureExperiment } from 'util/optimizely/featureExperimentation';
import { Flags } from 'util/optimizely/types';

import { useApplicationQuery } from '../Application/graphql/queries/customer/query';
import schema from './schema';
import { FormValues } from './types';

const INTERVIEW_URNS: Array<keyof FormValues> = [
  'urn:pg:data-point:insured-risk-activity-lived-or-traveled-overseas-past-two-years',
  'urn:pg:data-point:insured-risk-activity-lived-or-traveled-overseas-past-two-years-details',
  'urn:pg:data-point:insured-risk-activity-past-or-expect-travel-afghanistan-or-iraq',
  'urn:pg:data-point:insured-risk-activity-past-or-expect-travel-afghanistan-or-iraq-details',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-date-of-travel',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-destination',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-duration',
  'urn:pg:data-point:insured-risk-activity-travel-future-twelve-months-reason',
];

const Travel: React.FC = () => {
  const navigate = useNavigate();
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const { data, loading } = useGetDataPointValues({
    params: {
      urns: INTERVIEW_URNS,
    },
  });

  // MLD-365 Remove if Split Funnel V1 loses -- START
  const { enabled: isSplitFunnelV1 } = getFeatureExperiment(Flags.SPLIT_FUNNEL_V1);

  const activeLifeApplicationId = useSelector((app) => app.activeLifeApplicationId);

  const location = useLocation();

  const { data: customerData, loading: customerLoading } = useApplicationQuery({
    variables: {
      lifeApplicationId: activeLifeApplicationId,
      uuid: activeUserUuid,
      productUrns: [BANNER_RISETERM_URN, BANNER_HORIZON_URN],
    },
    skip: !isSplitFunnelV1,
  });
  // MLD-365 Remove if Split Funnel V1 loses -- END

  const { enabled: fieldUWUpdatesEnabled } = useFeatureExperiment(Flags.FIELD_UW_UPDATES); // MLD-409 Remove if field uw updated loses

  const { createDataPointValuesFromInterview } = useCreateDataPointValues();

  if (loading || customerLoading) {
    return <GlobalLoader />;
  }

  const onSubmitComplete = async (values: FormValuesOutput<FormValues>) => {
    const res = await createDataPointValuesFromInterview(values, schema);
    let path = APPLICATION_FULL_PATH;

    if (res.error?.message) {
      return;
    }
    completedNavigatorStep(StepName.TRAVEL, activeUserUuid);

    // MLD-409 Remove if field uw updated loses
    if (fieldUWUpdatesEnabled) {
      return navigate(MOTIVATIONS_FULL_PATH);
    }

    // MLD-365 Remove if Split Funnel V1 loses -- START
    if (isSplitFunnelV1 && !!customerData) {
      frontendAnalyticsTrack('Feature Experiment Viewed', {
        flag_key: 'life-digital_experiment_split_funnel_v1',
      });
      const userAge = customerData?.customer?.identityProfile.age ?? 0;
      const userIncome = customerData?.customer?.financialProfile.individualIncome ?? 0;
      const lgaPolicy = customerData.lifeApplication.policies.quotedPolicies[0];
      const isEligibleForDigital =
        lgaPolicy &&
        isEligibleForApplyOnline({
          productUrn: lgaPolicy?.productUrn,
          lifeApplicationSource: customerData.lifeApplication.source,
          customerAge: userAge,
          coverageAmount: lgaPolicy.coverageAmount,
          tableRating: lgaPolicy.tableRating,
        });
      const userAttributes = JSON.parse(customerData?.customer?.userAttributes ?? '{}');

      const { nextRoute } = handleSplitFunnel({
        maritalStatus: !!userAttributes.life_shopper_questions?.married,
        currentRoute: location.pathname,
        income: userIncome,
        age: userAge,
        digitalEligibility: isEligibleForDigital,
        lifeApplicationSource: customerData.lifeApplication.source,
      });

      path = nextRoute!;

      return navigate(path);
    }
    // MLD-365 Remove if Split Funnel V1 loses -- END

    navigate(path);
  };

  return (
    <NavigatorContent>
      <DynamicFormWrapper>
        <DynamicForm<FormValues>
          name="travel"
          schema={schema}
          initialValues={data?.dataPointValues}
          onSubmit={onSubmitComplete}
        />
      </DynamicFormWrapper>
    </NavigatorContent>
  );
};

export default Travel;
